import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Paper,
  Tab,
  Tabs,
} from '@mui/material';
import { 
  Phone as PhoneIcon, 
  Email as EmailIcon,
  Info as InfoIcon,
  MarkEmailRead as ContactFormIcon,
  NewReleases as ReleaseNotesIcon,
  School as TrainingIcon,
} from '@mui/icons-material';
import PageContainer from '../components/PageContainer';
import ReleaseNotesList from '../components/ReleaseNotesList';
import ContactForm from '../components/ContactForm';
import TrainingTab from '../components/help/TrainingTab';
import { useLocation, useNavigate } from 'react-router-dom';

const Help: React.FC = () => {
  const [activeTab, setActiveTab] = React.useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  // Map tab names to indices
  const tabMap = {
    'contact': 0,
    'form': 1,
    'release-notes': 2,
    'training': 3
  } as const;

  // Map indices to tab names
  const reverseTabMap = {
    0: 'contact',
    1: 'form',
    2: 'release-notes',
    3: 'training'
  } as const;

  // Set initial tab from URL on mount
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam && tabParam in tabMap) {
      setActiveTab(tabMap[tabParam as keyof typeof tabMap]);
    }
  }, [location.search]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Update URL with new tab
    const newTab = reverseTabMap[newValue as keyof typeof reverseTabMap];
    navigate(`/app/help?tab=${newTab}`, { replace: true });
  };

  return (
    <PageContainer>
      <Typography 
        variant="subtitle1" 
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        Help & Support
      </Typography>
      
      <Paper elevation={2}>
        <Box>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            indicatorColor="primary"
            textColor="primary"
            aria-label="help tabs"
          >
            <Tab icon={<InfoIcon />} label="Contact Information" />
            <Tab icon={<ContactFormIcon />} label="Contact Form" />
            <Tab icon={<ReleaseNotesIcon />} label="Release Notes" />
            <Tab icon={<TrainingIcon />} label="Training" />
          </Tabs>
        </Box>
        
        <Box role="tabpanel" hidden={activeTab !== 0} className="p-6">
          {activeTab === 0 && (
            <Box>             
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Box className="flex items-start mb-4">
                    <PhoneIcon color="primary" className="mr-3 mt-1" />
                    <Box>
                      <Typography variant="h6">Phone Support</Typography>
                      <Typography variant="body1" className="font-bold mt-1">
                        (303) 500-0430
                      </Typography>
                      <Typography variant="body2" color="text.secondary" className="mt-2">
                        Available Monday - Friday, 9am - 5pm Mountain Time
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box className="flex items-start mb-4">
                    <EmailIcon color="primary" className="mr-3 mt-1" />
                    <Box>
                      <Typography variant="h6">Email Support</Typography>
                      <Typography variant="body1" className="mt-1">
                        <a href="mailto:support@travelminds.com" className="text-blue-600">
                          support@travelminds.com
                        </a>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" className="mt-2">
                        We normally respond within 1 business day
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              
              <Box className="mt-8 pt-4 border-t border-gray-200">
                <Typography variant="body1" className="mb-4">
                  Our dedicated support team is ready to assist you with any questions or issues you may encounter 
                  while using our platform.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  For urgent matters, please call us directly. For general inquiries or non-urgent issues, 
                  you can use the contact form in the next tab or send us an email.
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        
        <Box role="tabpanel" hidden={activeTab !== 1} className="p-6">
          {activeTab === 1 && (
            <ContactForm />
          )}
        </Box>
        
        <Box role="tabpanel" hidden={activeTab !== 2} className="p-6">
          {activeTab === 2 && (
            <Box>
              <Typography variant="h5" gutterBottom>Recent Updates & Improvements</Typography>
              <Divider className="mb-4" />
              <Typography variant="body1" className="mb-4">
                Stay up to date with the latest features, improvements, and bug fixes in our platform.
                We regularly update Travel Minds to improve your experience and provide new capabilities.
              </Typography>
              
              <ReleaseNotesList 
                defaultExpanded={true}
                showTitle={false}
                includeHidden={false}
              />
            </Box>
          )}
        </Box>

        <Box role="tabpanel" hidden={activeTab !== 3} className="p-6">
          {activeTab === 3 && (
            <TrainingTab />
          )}
        </Box>
      </Paper>
    </PageContainer>
  );
};

export default Help;
